import { useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useBranding } from '../../providers/BrandingProvider';

interface SignupHeaderLogosProps {
  // `regular` is the desktop header; `compact` is the tablet and mobile
  // headers, where the logos share a row with Log Out and (on tablet) the
  // "Secure Application" badge.
  size: 'regular' | 'compact';
}

// Every /branding/me call signs the logo URL again, so the same S3 object comes
// back with a new query string each time. Failure and retry are tracked by the
// object (the URL without its query string), not by the signed URL.
const logoObject = (url: string) => url.split('?')[0];

// The TACH logo on every signup application header, plus the co-branded
// partner logo beside it when /branding/me returned an application logo
// (THT-809). With no partner logo this renders exactly the TACH <img> the
// header always had, so a regular TACH applicant sees no change.
function SignupHeaderLogos({ size }: SignupHeaderLogosProps) {
  const { applicationLogoUrl, refreshBranding } = useBranding();
  // Remember which logo failed rather than a bare boolean, so a later fetch
  // that returns a different logo gets a fresh attempt.
  const [failedObject, setFailedObject] = useState<string | null>(null);
  // The logo we already asked a freshly signed URL for. A logo that is really
  // broken costs one extra request, not a loop: the re-signed URL has the same
  // object, so a second failure is final.
  const refreshedFor = useRef<string | null>(null);

  const isCompact = size === 'compact';

  const tachLogo = (
    <img
      src="/TachLogo.svg"
      alt="Tach Logo"
      // The desktop header never had shrink-0 on this image; the tablet and
      // mobile headers always did. Kept as two whole strings so the markup
      // stays exactly what each header rendered before.
      className={
        isCompact ? 'h-[25px] w-[100px] shrink-0' : 'h-[25px] w-[100px]'
      }
    />
  );

  if (!applicationLogoUrl || failedObject === logoObject(applicationLogoUrl)) {
    return tachLogo;
  }

  // The logo URL is presigned and expires, so a failed load is first retried
  // with a freshly signed one. Only when that does not help is the partner
  // image hidden.
  const handleError = async () => {
    const failedUrl = applicationLogoUrl;
    const failed = logoObject(failedUrl);
    // Hiding the logo is silent for the applicant, so report it: a partner set
    // up with a wrong logo key or an unusable file would otherwise leave every
    // co-branded applicant on a TACH-only header with nobody told. The object
    // path carries no signature, so nothing sensitive is sent.
    const giveUp = () => {
      Sentry.captureMessage('Partner application logo failed to load', {
        level: 'warning',
        tags: { branding: 'application-logo-failed', logo_object: failed },
      });
      setFailedObject(failed);
    };
    if (refreshedFor.current === failed) {
      giveUp();
      return;
    }
    refreshedFor.current = failed;
    const fresh = await refreshBranding();
    // No new URL (or the very same one, which the browser will not retry):
    // nothing left to try.
    if (!fresh || fresh === failedUrl) giveUp();
  };

  return (
    <div className="flex min-w-0 items-center gap-3">
      {tachLogo}
      <span
        aria-hidden="true"
        data-testid="signup-header-partner-divider"
        className={`w-px shrink-0 bg-gray-300 ${isCompact ? 'h-5' : 'h-6'}`}
      />
      <img
        src={applicationLogoUrl}
        alt="Partner logo"
        // Only the partner image is ever hidden: a broken partner logo must
        // leave the TACH logo (and the header) exactly as it was.
        onError={handleError}
        // A load that works re-arms the single retry for the next expiry.
        onLoad={() => {
          refreshedFor.current = null;
        }}
        // min-w-0 + shrink let a wide wordmark give way on a narrow phone
        // instead of pushing Log Out off the screen.
        className={
          isCompact
            ? 'max-h-[22px] w-auto min-w-0 max-w-[150px] shrink object-contain'
            : 'max-h-[28px] w-auto min-w-0 max-w-[200px] shrink object-contain'
        }
      />
    </div>
  );
}

export default SignupHeaderLogos;
